import './Modal.scss';

import classnames from 'classnames';
import React from 'react';

import { useCartApi } from './api/useCartApi';
import { useAppStaticContext } from './AppContext';
import { Button, ButtonProps } from './Button';
import { appConfig } from './config';
import { PopupMinimizedContentSection, PopupTypeV2ButtonCallbackRedirect } from './contentTypes';
import { useActionToProps } from './Cta';
import { useEqualHeightContextStrict } from './EqualHeightContext';
import { Breakpoint } from './interfaces';
import { ModalContext, ModalContextInterface, useModalContext } from './ModalContext';
import { PopupAvailableActions } from './popup/popupHelper';
// Ho dovuto importare direttamente useNavigate dal suo file perché in router.ts c'è l'import di Link che usa Modal e quindi si crea una dipendenza circolare.
import { useNavigate } from './router/useNavigate';
import { useModals } from './useModals';
import { createUUID, renderUI, uiClassName, wait } from './util';
import { PropsWithCustomComponent, VariantsController } from './VariantsController';

export type ModalSize = 'small' | 'default' | 'large' | 'extra-large';
export type ModalFullScreen = Exclude<Breakpoint, 'xs'> | 'always';

export interface ModalProps {
  autoClose?: number; // in seconds
  backdrop?: boolean | 'static';
  centered?: boolean;
  children?: React.ReactNode | ((modalContext: ModalContextInterface) => React.ReactNode);
  className?: string;
  closable?: boolean;
  delay?: number; // in seconds
  fade?: boolean;
  fullscreen?: ModalFullScreen;
  innerRef?: React.RefObject<HTMLDivElement>;
  isMinimized?: boolean;
  minimizable?: boolean;
  onClose?(): void;
  onOpen?(): void;
  scrollable?: boolean;
  size?: ModalSize;
}

export const Modal = (props: ModalProps) => {
  const id = React.useMemo(() => createUUID(), []);
  return <ModalWithId {...props} id={id} />;
};

export interface ModalWithIdProps extends ModalProps {
  id: string;
}

export const ModalWithId = ({
  autoClose,
  backdrop,
  centered,
  children,
  className,
  closable = true,
  delay,
  fade = true,
  fullscreen,
  id,
  innerRef,
  onClose,
  onOpen,
  scrollable,
  size,
}: ModalWithIdProps) => {
  const { addEventListener, close } = useModals();

  React.useEffect(() => {
    if (onOpen) {
      return addEventListener(id, 'shown', 'modal', onOpen);
    }
  }, [addEventListener, id, onOpen]);

  React.useEffect(() => {
    if (onClose) {
      return addEventListener(id, 'hidden', 'modal', onClose);
    }
  }, [addEventListener, id, onClose]);

  React.useEffect(() => {
    autoClose &&
      addEventListener(id, 'shown', 'modal', () => {
        wait(autoClose * 1000).then(() => {
          close(id);
        });
      });
  }, [addEventListener, autoClose, close, id, onClose]);

  const equalHeightContext = useEqualHeightContextStrict();
  const { updateTrigger } = equalHeightContext ?? {};

  React.useEffect(() => {
    return addEventListener(id, 'shown', 'modal', () => {
      updateTrigger && updateTrigger();
    });
  }, [addEventListener, id, updateTrigger]);

  return (
    <ModalContext id={id}>
      {(modalContext) =>
        renderUI({
          bs5: (
            <div
              aria-hidden="true"
              aria-labelledby={`${id}-label`}
              className={classnames('modal', { fade: fade }, className)}
              data-bs-backdrop={!closable ? 'static' : backdrop}
              data-bs-keyboard={!closable ? false : true}
              id={id}
              ref={innerRef}
              role="dialog"
              tabIndex={-1}
            >
              <div
                className={classnames(
                  'modal-dialog',
                  { 'modal-dialog-scrollable': scrollable },
                  { 'modal-dialog-centered': centered },
                  { 'modal-sm': size === 'small' },
                  { 'modal-lg': size === 'large' },
                  { 'modal-xl': size === 'extra-large' },
                  { 'modal-fullscreen-sm-down': fullscreen === 'sm' },
                  { 'modal-fullscreen-md-down': fullscreen === 'md' },
                  { 'modal-fullscreen-lg-down': fullscreen === 'lg' },
                  { 'modal-fullscreen-xl-down': fullscreen === 'xl' },
                  { 'modal-fullscreen-xxl-down': fullscreen === 'xxl' },
                  { 'modal-fullscreen': fullscreen === 'always' }
                )}
              >
                <div className="modal-content">
                  {typeof children === 'function' ? children(modalContext) : children}
                </div>
              </div>
            </div>
          ),
        })
      }
    </ModalContext>
  );
};

export interface PackedModalProps extends ModalWithIdProps {
  bodyClassName?: string;
  buttons?: Array<ModalButtonProps>;
  code?: string;
  footerClassName?: string;
  headerClassName?: string;
  minimizedProps?: PopupMinimizedContentSection;
  title: string;
}

export interface ModalHeaderProps {
  className?: string;
  closable?: boolean;
  minimizable?: boolean;
  title: string;
}

const ModalHeader = ({ className, closable = true, minimizable, title }: ModalHeaderProps) => {
  const { id, minimize } = useModalContext();
  return renderUI({
    bs5: (
      <div className={classnames(className, 'modal-header')}>
        <h5 className="modal-title" id={`${id}-label`}>
          {title}
        </h5>
        <div className={uiClassName({ bs5: 'd-flex gap-3' })}>
          {minimizable && (
            <Button
              aria-label="Minimize"
              className="minimize-button"
              iconEnd={appConfig.icons ? { name: appConfig.icons.minimize || '' } : undefined}
              onClick={minimize}
              type="button"
              variant="blank"
            />
          )}
          {closable && (
            <Button
              aria-label="Close"
              className="close-button"
              data-bs-dismiss="modal"
              iconEnd={appConfig.icons ? { name: appConfig.icons.close } : undefined}
              type="button"
              variant="blank"
            />
          )}
        </div>
      </div>
    ),
  });
};

export interface ModalBodyProps {
  children?: React.ReactNode;
  className?: string;
}

export const ModalBody = ({ children, className }: ModalBodyProps) => {
  return renderUI({
    bs5: <div className={classnames(className, 'modal-body')}>{children}</div>,
  });
};

export interface ModalFooterProps {
  children?: React.ReactNode;
  className?: string;
}

const ModalFooter = ({ children, className }: ModalFooterProps) => {
  return renderUI({
    bs5: <div className={classnames(className, 'modal-footer')}>{children}</div>,
  });
};

export interface ModalButtonProps extends ButtonProps<string, string, number> {
  availableActions?: PopupAvailableActions;
  callbacks?: Array<{
    action?: string;
    close?: boolean;
    code?: string;
    couponCode?: string;
    externalUrl?: string;
    redirect?: PopupTypeV2ButtonCallbackRedirect;
  }>;
  // Mantengo il close anche se è duplicato con la callbacks per retrocompatibilità
  close?: boolean;
}

export const ModalButton = ({ availableActions, callbacks, close, onClick, ...otherProps }: ModalButtonProps) => {
  const { applyCoupon } = useCartApi();
  const { close: closeModal } = useModalContext();
  const { getPagePath } = useAppStaticContext();
  const { getActionProps } = useActionToProps();
  const navigate = useNavigate();

  const handleButtonClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      onClick && onClick(event);

      let isClosed = false;
      if (callbacks) {
        for (const cb of callbacks) {
          if (cb.close) {
            closeModal();
            isClosed = true;
          }

          if (cb.redirect) {
            if (cb.redirect.fixedPath) {
              const actionProps = getActionProps(cb.redirect.fixedPath);
              if (actionProps?.to) {
                navigate(actionProps.to);
              } else if (actionProps?.onClick) {
                actionProps.onClick();
              }
            } else if (cb.redirect.code) {
              const pagePath = getPagePath(cb.redirect.code);
              pagePath && navigate(pagePath);
            } else if (cb.redirect.href) {
              navigate(cb.redirect.href);
            }
          }

          cb.externalUrl && window.open(cb.externalUrl, '_blank', 'noopener,noreferrer');

          cb.couponCode && applyCoupon({ couponCode: cb.couponCode });

          cb.action && availableActions?.[cb.action]?.();
        }
      }

      // Mantengo il close fuori dalle callback per retrocompatibilità, anche se è duplicato
      if (close && !isClosed) {
        closeModal();
      }
    },
    [callbacks, close, closeModal, onClick, navigate, applyCoupon, getPagePath, getActionProps, availableActions]
  );

  return <Button {...otherProps} onClick={handleButtonClick} />;
};

export type ModalHeaderVariants = '';

const ModalHeaderController = (props: PropsWithCustomComponent<ModalHeaderProps>) => (
  <VariantsController<ModalHeaderProps, ModalHeaderVariants>
    {...props}
    variantsControllerConfig={{
      defaultComponent: ModalHeader,
      name: 'ModalHeader',
    }}
  />
);
export { ModalHeaderController as ModalHeader };

export type ModalFooterVariants = '';

const ModalFooterController = (props: PropsWithCustomComponent<ModalFooterProps>) => (
  <VariantsController<ModalFooterProps, ModalFooterVariants>
    {...props}
    variantsControllerConfig={{
      defaultComponent: ModalFooter,
      name: 'ModalFooter',
    }}
  />
);

export { ModalFooterController as ModalFooter };

export type UseFormModalProps = Partial<Omit<PackedModalProps, 'children' | 'id'>>;

// export type UseFormModal<FormProps> = (modalProps?: UseFormModalProps) => (formProps?: FormProps) => void;
